import {
  sanitizeMobileStripBandSettings,
  type MobileStripBandSettings,
} from "../../mobileStripBandSettings"
import savedBandSettingsJson from "../../mobileStripBandSettings.json"
import { sampleSeriesAt, type TimeValuePoint } from "../interpolate"
import { timestampFromYearFraction } from "../storyTime"
import {
  FLOP_END_YEAR_FRACTION,
  MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION,
} from "../camera/planFrameModes"
import { DESKTOP_VIEW_Y_MIN, type StripCamera } from "../camera/useStripCamera"

// The authored band look + line relationship (edited on /zoom-editor).
export const SAVED_BAND_SETTINGS =
  sanitizeMobileStripBandSettings(savedBandSettingsJson)

// The takeover-risk band's boundaries in VALUE space (screen rendering
// is bandSvgLayers / DitherFieldCanvas).
export type RiskBandModel = {
  anchorT: number
  // Lower boundary, density-ramp top, and solid-block top.
  minValueAt: (t: number) => number
  maxValueAt: (t: number) => number
  topValueAt: (t: number) => number
}

type RiskBandInputs = {
  isDesktopMirror: boolean
  band: MobileStripBandSettings
  boundsMinSeries: TimeValuePoint[]
  boundsMaxSeries: TimeValuePoint[]
  lineValueAt: (time: number) => number
  camera: StripCamera
}

// squish: the real lossOfControlByPlan curves. desktop: a generated
// hug-the-line band — the real min curve rescaled so it passes
// (line + offset) at the anchor date, ceilinged at the frame top;
// max = min + density ramp. The boundary keeps its own gentle shape and
// never bends to chase the line — the line rises INTO the red instead
// (Joanna's 2029-beat direction), then the growing frame brings it down.
export const createRiskBandModel = (inputs: RiskBandInputs): RiskBandModel => {
  const frameSpan = inputs.camera.frameYMax - DESKTOP_VIEW_Y_MIN
  const anchorT = bandAnchorTime(inputs)
  const minValueAt = makeMinValueAt(
    inputs,
    anchorT,
    frameSpan * inputs.band.bandOffsetSpanFraction,
  )
  const maxValueAt = makeMaxValueAt(
    inputs,
    minValueAt,
    frameSpan * inputs.band.bandRampSpanFraction,
  )
  const topValueAt = makeTopValueAt(inputs, minValueAt, maxValueAt)
  return { anchorT, minValueAt, maxValueAt, topValueAt }
}

// Until the flop beat the band is anchored at the AUTHORED beat (2029),
// a constant — the field holds one fixed shape, pinned just above where
// the line stands at the choice, so the projected trajectory visibly
// climbs INTO the red while the view is frozen. Frame-freezing modes
// freeze the anchor too: a live anchor rescales the field to ride
// line+offset, so the fooming line "pushed the red off" ahead of itself.
// "freeze" keeps the constant anchor forever (the line pierces the fixed
// field and exits the top); "flop-freeze" rides the anchor through the
// flop, then pins it where the frame froze.
const bandAnchorTime = ({ isDesktopMirror, band, camera }: RiskBandInputs): number => {
  if (!isDesktopMirror) return camera.anchorT
  const isPreFlop =
    camera.storyYearFraction < MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION
  if (isPreFlop || camera.planFrameMode === "freeze") {
    return timestampFromYearFraction(band.bandAnchorYearFraction)
  }
  if (camera.planFrameMode === "flop-freeze") {
    return Math.min(
      camera.anchorT,
      timestampFromYearFraction(FLOP_END_YEAR_FRACTION - 0.001),
    )
  }
  return camera.anchorT
}

const makeMinValueAt = (
  { isDesktopMirror, boundsMinSeries, lineValueAt, camera }: RiskBandInputs,
  anchorT: number,
  offsetAxisUnits: number,
): ((t: number) => number) => {
  if (!isDesktopMirror) return t => sampleSeriesAt(boundsMinSeries, t)
  // Rescale the data curve so it passes (line + offset) at the anchor.
  const scale = (lineValueAt(anchorT) + offsetAxisUnits)
    / Math.max(sampleSeriesAt(boundsMinSeries, anchorT), 1e-9)
  return t => Math.min(sampleSeriesAt(boundsMinSeries, t) * scale, camera.frameYMax)
}

const makeMaxValueAt = (
  { isDesktopMirror, boundsMaxSeries, camera }: RiskBandInputs,
  minValueAt: (t: number) => number,
  rampAxisUnits: number,
): ((t: number) => number) => {
  if (!isDesktopMirror) {
    return t => Math.max(sampleSeriesAt(boundsMaxSeries, t), minValueAt(t) * 1.0001)
  }
  return t => Math.min(minValueAt(t) + rampAxisUnits, camera.frameYMax)
}

// The solid block's top: the configured fraction of the way from the
// lower to the upper boundary — linear on the desktop axis,
// log-interpolated on the squish scale.
const makeTopValueAt = (
  { isDesktopMirror, band }: RiskBandInputs,
  minValueAt: (t: number) => number,
  maxValueAt: (t: number) => number,
): ((t: number) => number) => {
  if (isDesktopMirror) {
    return t => {
      const lo = minValueAt(t)
      return lo + (maxValueAt(t) - lo) * band.bandThicknessFraction
    }
  }
  return t => {
    const logLo = Math.log(minValueAt(t))
    return Math.exp(
      logLo + (Math.log(maxValueAt(t)) - logLo) * band.bandThicknessFraction,
    )
  }
}
